// VB-019 — Welcome modal: shown once at first login
import { X, Layers, List, Network } from 'lucide-react';

interface OnboardingWelcomeModalProps {
  isOpen: boolean;
  onStart: () => void;
  onSkip: () => void;
}

export function OnboardingWelcomeModal({ isOpen, onStart, onSkip }: OnboardingWelcomeModalProps) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onSkip}
    >
      <div
        className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-8">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div className="w-10 h-10 rounded-xl bg-zinc-100 text-zinc-950 flex items-center justify-center font-bold text-lg shrink-0">
              S
            </div>
            <button
              onClick={onSkip}
              className="text-zinc-500 hover:text-zinc-300 transition-colors ml-4 mt-0.5"
              aria-label="Fermer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <h2 className="text-xl font-semibold text-zinc-100 mb-1.5">
            Bienvenue sur Visual Brain
          </h2>
          <p className="text-zinc-400 text-sm mb-7">
            Un espace unique pour organiser votre pensée, vos projets et vos idées en un seul endroit.
          </p>

          {/* 3 value propositions */}
          <div className="space-y-3 mb-8">
            <div className="flex items-start gap-4 p-4 rounded-xl bg-zinc-800/50 border border-zinc-700/30">
              <div className="w-8 h-8 rounded-lg bg-blue-500/15 text-blue-400 flex items-center justify-center shrink-0 mt-0.5">
                <Layers className="w-4 h-4" />
              </div>
              <div>
                <p className="text-sm font-medium text-zinc-100 mb-1">Objets structurés</p>
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Tâches, notes, objectifs, événements et contacts — chacun typé, retrouvable et filtrable.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-xl bg-zinc-800/50 border border-zinc-700/30">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/15 text-emerald-400 flex items-center justify-center shrink-0 mt-0.5">
                <List className="w-4 h-4" />
              </div>
              <div>
                <p className="text-sm font-medium text-zinc-100 mb-1">Vue liste et canvas</p>
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Chaque élément peut être vu en liste ou placé sur le Visual Brain. Même donnée, deux façons de la voir.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-xl bg-zinc-800/50 border border-zinc-700/30">
              <div className="w-8 h-8 rounded-lg bg-violet-500/15 text-violet-400 flex items-center justify-center shrink-0 mt-0.5">
                <Network className="w-4 h-4" />
              </div>
              <div>
                <p className="text-sm font-medium text-zinc-100 mb-1">Connexions visuelles</p>
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Reliez vos éléments entre eux sur le canvas pour visualiser comment vos projets s'articulent.
                </p>
              </div>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex items-center gap-3">
            <button
              onClick={onSkip}
              className="flex-1 px-4 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 hover:text-zinc-100 hover:border-zinc-600 text-sm font-medium transition-colors"
            >
              Passer
            </button>
            <button
              onClick={onStart}
              className="flex-1 px-4 py-2.5 rounded-xl bg-zinc-100 hover:bg-zinc-200 text-zinc-950 text-sm font-medium transition-colors"
            >
              Commencer le tour →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
